import { Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router';
import { AccessControlAction, AppRoutes } from '@/constants';
import {
  ActiveUsersPage,
  AiProcessedDocuments,
  CaseItemLayout,
  CaseListPage,
  ChronologyPageContainer,
  DeactivatedUsersPage,
  FullBundlePageContainer,
  GlobalAdminDashboardPage,
  LightBundlePageContainer,
  LoginPage,
  MedicalRecordsContainer,
  MicrosoftLoginRedirect,
  TimelinePageContainer,
  UploadedMedicalRecords,
  UsersManagementLayout,
} from './pages';
import { GdprCheckPage } from './pages/GdprCheck/GdprCheckPage';
import { useEffect } from 'react';
import { setNavigateMiddleware } from '@/api';
import { useAccessControl, useCheckLoginStatus, useEndpointLockToasts, usePreventUnloadWhenLoading } from '@/hooks';
import { BroadcastService, FLETCHERS_CHANNEL } from '@/broadcast';
import { globalToastService } from '@repo/atomic-components';
import { GDPR_REVIEW_COMPLETED_KEY } from './pages/GdprCheck/constants';
import { useRouteTitle } from '@/hooks';
import { useLazyGetUserAboutQuery } from '@/api/users/userAbout';
import { selectUserAboutData, useAppSelector } from '@/store';

const RedirectToAiProcessedDocuments = () => {
  const { caseId } = useParams();
  return <Navigate to={`/case-list/${caseId}/${AppRoutes.AiProcessedDocuments}`} replace />;
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  usePreventUnloadWhenLoading();
  useCheckLoginStatus();
  useRouteTitle();
  useEndpointLockToasts();

  const userAboutData = useAppSelector(selectUserAboutData);

  const isViewUserManagementTab = useAccessControl(AccessControlAction.VIEW_USER_MANAGEMENT_TAB);
  const isViewMedicalRecordsTab = useAccessControl(AccessControlAction.VIEW_MEDICAL_RECORDS_TAB);
  const isViewBundles = useAccessControl(AccessControlAction.VIEW_BUNDLES);
  const isGlobalAdminOnly = useAccessControl(AccessControlAction.GLOBAL_ADMIN_ONLY);

  const [triggerGetUserAbout] = useLazyGetUserAboutQuery();

  useEffect(() => {
    BroadcastService.getInstance(FLETCHERS_CHANNEL);
    triggerGetUserAbout({});

    const handleStorageEvent = (event: StorageEvent) => {
      if (event.key === GDPR_REVIEW_COMPLETED_KEY && event.newValue) {
        localStorage.removeItem(GDPR_REVIEW_COMPLETED_KEY);
        globalToastService.show({
          severity: 'success',
          summary: 'GDPR check completed successfully.',
        });
      }
    };
    window.addEventListener('storage', handleStorageEvent);
    return () => window.removeEventListener('storage', handleStorageEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setNavigateMiddleware(navigate);
  }, [navigate]);

  useEffect(() => {
    if (
      userAboutData &&
      ((!isViewUserManagementTab && pathname.includes(AppRoutes.UserManagement)) ||
        (!isViewMedicalRecordsTab && pathname.includes(AppRoutes.MedicalRecords)) ||
        (!isViewBundles && (pathname.includes(AppRoutes.FullBundle) || pathname.includes(AppRoutes.LightBundle))))
    ) {
      navigate(`/${AppRoutes.CaseList}`, { replace: true });
    }
  }, [isViewBundles, isViewMedicalRecordsTab, isViewUserManagementTab, location, navigate, pathname, userAboutData]);

  if (!userAboutData && !pathname.includes(AppRoutes.Login) && !pathname.includes(AppRoutes.LoginRedirect)) {
    return null;
  }

  return (
    <>
      <Routes>
        {/* AUTH */}
        {/* /login */}
        <Route path={AppRoutes.Login} element={<LoginPage />} />
        {/* /login/redirect */}
        <Route path={AppRoutes.LoginRedirect} element={<MicrosoftLoginRedirect />} />
        {/* MAIN CASE LIST ROUTE */}
        {/* /case-list */}
        <Route path={AppRoutes.CaseList}>
          <Route index element={<CaseListPage />} />

          {/* CASE DETAILS with <Outlet /> */}
          {/* /case-list/:caseId */}
          <Route path=":caseId" element={<CaseItemLayout />}>
            <Route index element={<Navigate to={AppRoutes.UploadedMedicalRecords} replace />} />

            {/* /case-list/:caseId/uploaded-medical-records */}
            <Route path={AppRoutes.UploadedMedicalRecords} element={<UploadedMedicalRecords />} />
            {/* /case-list/:caseId/ai-processed-documents */}
            <Route path={AppRoutes.AiProcessedDocuments} element={<AiProcessedDocuments />} />

            {/* REDIRECT /full-bundle, /light-bundle, /timeline or /chronology without ID */}
            {/* /case-list/:caseId/ai-processed-documents/full-bundle */}
            <Route
              path={`${AppRoutes.AiProcessedDocuments}/${AppRoutes.FullBundle}`}
              element={<RedirectToAiProcessedDocuments />}
            />
            {/* /case-list/:caseId/ai-processed-documents/light-bundle */}
            <Route
              path={`${AppRoutes.AiProcessedDocuments}/${AppRoutes.LightBundle}`}
              element={<RedirectToAiProcessedDocuments />}
            />
            {/* /case-list/:caseId/ai-processed-documents/timeline */}
            <Route
              path={`${AppRoutes.AiProcessedDocuments}/${AppRoutes.Timeline}`}
              element={<RedirectToAiProcessedDocuments />}
            />
            {/* /case-list/:caseId/ai-processed-documents/chronology */}
            <Route
              path={`${AppRoutes.AiProcessedDocuments}/${AppRoutes.Chronology}`}
              element={<RedirectToAiProcessedDocuments />}
            />
          </Route>

          {/* BUNDLES, TIMELINES or CHRONOLOGIES WITH ID out of :caseId <Outlet /> layout */}
          {/* /case-list/:caseId/ai-processed-documents/full-bundle/:fullBundleId */}
          <Route
            path={`:caseId/${AppRoutes.AiProcessedDocuments}/${AppRoutes.FullBundle}/:fullBundleId`}
            element={isViewBundles ? <FullBundlePageContainer /> : <Navigate to={`/${AppRoutes.CaseList}`} replace />}
          />
          {/* /case-list/:caseId/ai-processed-documents/light-bundle/:lightBundleId */}
          <Route
            path={`:caseId/${AppRoutes.AiProcessedDocuments}/${AppRoutes.LightBundle}/:lightBundleId`}
            element={isViewBundles ? <LightBundlePageContainer /> : <Navigate to={`/${AppRoutes.CaseList}`} replace />}
          />
          {/* /case-list/:caseId/ai-processed-documents/timeline/:timelineId */}
          <Route
            path={`:caseId/${AppRoutes.AiProcessedDocuments}/${AppRoutes.Timeline}/:timelineId`}
            element={<TimelinePageContainer />}
          />
          {/* /case-list/:caseId/ai-processed-documents/chronology/:chronologyId */}
          <Route
            path={`:caseId/${AppRoutes.AiProcessedDocuments}/${AppRoutes.Chronology}/:chronologyId`}
            element={<ChronologyPageContainer />}
          />
        </Route>
        {/* USER MANAGEMENT with <Outlet /> */}
        {/* /user-management */}
        <Route
          path={AppRoutes.UserManagement}
          element={
            isViewUserManagementTab ? <UsersManagementLayout /> : <Navigate to={`/${AppRoutes.CaseList}`} replace />
          }
        >
          <Route index element={<Navigate to={AppRoutes.ActiveUsers} replace />} />

          {/* /user-management/active-users */}
          <Route path={AppRoutes.ActiveUsers} element={<ActiveUsersPage />} />
          {/* /user-management/deactivated-users */}
          <Route path={AppRoutes.DeactivatedUsers} element={<DeactivatedUsersPage />} />
        </Route>
        {/* GLOBAL ADMIN DASHBOARD */}
        {/* /global-admin-dashboard */}
        <Route
          path={AppRoutes.GlobalAdminDashboard}
          element={
            isGlobalAdminOnly ? <GlobalAdminDashboardPage /> : <Navigate to={`/${AppRoutes.CaseList}`} replace />
          }
        />
        {/* MEDICAL RECORDS */}
        {/* /medical-records */}
        <Route path={AppRoutes.MedicalRecords}>
          <Route
            index
            element={
              isViewMedicalRecordsTab ? <MedicalRecordsContainer /> : <Navigate to={`/${AppRoutes.CaseList}`} replace />
            }
          />
        </Route>
        {/* /gdpr/:documentId */}
        <Route path={`${AppRoutes.GdprCheck}/:documentId`} element={<GdprCheckPage />} />
        {/* FALLBACK */}
        <Route path="*" element={<Navigate to={`/${AppRoutes.CaseList}`} replace />} />
      </Routes>
    </>
  );
}

export default App;
